import { GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { OpenGelderlandHomePageFieldsFragment, OpenGelderlandInformationFieldsFragment } from 'api/graphql-types';
import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import { getServerPageGetOpenGelderlandHome } from 'api/graphql-helper';
import { initialState as settingsInitialState } from 'redux/stores/settings';
import { IBasePageProps } from 'types/page';
import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import Layout from 'modules/layout/layout';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import { Head } from 'modules/head/head';
import IntroHeader, { IModule } from 'modules/intro-header/intro-header';
import Collections from 'modules/collections/collections';
import { BaseModule } from 'modules/base-module/base-module';

interface IOpenGelderlandPageProps extends IBasePageProps {
	openGelderlandHomepage?: OpenGelderlandHomePageFieldsFragment;
	openGelderlandInformation?: OpenGelderlandInformationFieldsFragment;
}

const OpenGelderlandHomePage = (props: IOpenGelderlandPageProps): React.JSX.Element => {
	const router = useRouter();
	const modules = props.openGelderlandHomepage?.modules;
	const seoModule = props?.openGelderlandHomepage?.seo;
	const collectionsModule = props?.openGelderlandHomepage?.collections;

	const onSearch = async (search: string): Promise<void> => {
		router.push({ pathname: '/woo-documenten', query: { zoekterm: search } });
	};

	return (
		<Layout
			navbar={props?.openGelderlandInformation?.navbar}
			footer={props?.openGelderlandInformation?.footer}
			customSearch={onSearch}
		>
			<Head seoModule={seoModule} seoParent={props?.openGelderlandInformation?.seo} />
			<BaseModule defaultModuleOptions={{ padding: 'none', backgroundColor: 'white' }}>
				<IntroHeader showBreadCrumbs={false} module={props?.openGelderlandHomepage?.header as IModule} slug={'/'} />
			</BaseModule>
			<BaseModule defaultModuleOptions={collectionsModule?.defaultModuleOptions}>
				<Collections module={collectionsModule} />
			</BaseModule>
			<ModuleBuilder modules={modules} slug={'/'} />
		</Layout>
	);
};

export const getStaticProps: GetStaticProps<IBasePageProps> = async (ctx) => {
	try {
		const { locale, publicationState, draftMode } = getStrapiParametersFromContext(ctx);

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const openGelderlandHomePageResult = await getServerPageGetOpenGelderlandHome(
			{
				variables: {
					locale,
					publicationState
				}
			},
			client
		);
		const { openGelderlandHomepage, openGelderlandInformation } = openGelderlandHomePageResult.props.data;

		if (openGelderlandHomepage == null) {
			return {
				notFound: true
			};
		}

		return {
			props: {
				openGelderlandHomepage: openGelderlandHomepage.data?.attributes,
				openGelderlandInformation: openGelderlandInformation?.data?.attributes,
				initialReduxState: {
					settings: {
						...settingsInitialState,
						publicationState,
						draftMode,
						contentType: 'open-gelderland-homepage',
						contentTypeId: openGelderlandHomepage?.data?.documentId,
						showNeutralStyle: false,
						hideLanguageSwitcher: true,
						hideSearchbar: false,
						isSubsite: true
					}
				}
			},
			revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_TIME_IN_SECONDS)
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('error in Open Gelderland Home Page', error);
		return {
			notFound: true
		};
	}
};

export default OpenGelderlandHomePage;
